"use client";

import { FC, useState } from "react";
import BFLTabs from "@repo/shared-ui/BFLTabs";
import TechnicalSummaryTable from "./StockTechnicalSummaryTable";
import {
  EmaSmaTypes,
  STOCK_TECHNICAL_SUMMARY_GRAPH,
  STOCK_TECHNICAL_SUMMARY_GRAPH_MOB,
  STOCK_TECHNICAL_SUMMARY_TABS,
  STOCK_TECHNICAL_TABS,
} from "./StockTechnicalSummaryConstant";
import { IStockTechnicalSummaryData } from "../../interfaces/StockTechnicalSummary.interface";
import BFLToggleButtonWrapper from "@repo/shared-ui/BFLToggleButtonWrapper";
import BFLToggleButton from "@repo/shared-ui/BFLToggleButton";
import dynamic from "next/dynamic";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import BFLLazyLoad from "@repo/shared-ui/BFLLazyLoad";
import { handleTechnicalSummaryTabDL } from "../StockFinancials/BFLTabsFunction";
import { selectLoginStatus, useAppSelector } from "@repo/web-store";
import useMediaQuery from "@repo/shared-ui/useMediaQuery";

const LoadingSkeleton = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  return <BFLSkeleton height={isMobile ? 145 : 285} />;
};

const StockTechnicalSummaryChart: any = dynamic(
  () => import("./StockTechnicalSummaryChart.tsx") as any,
  {
    ssr: false,
    loading: () => <LoadingSkeleton />,
  },
);

interface StockTechnicalSummaryClientWrapperProps {
  technicalSummaryData: IStockTechnicalSummaryData;
  bearBull: {
    bearish: number;
    bullish: number;
  };
}

const StockTechnicalSummaryClientWrapper: FC<
  StockTechnicalSummaryClientWrapperProps
> = ({ technicalSummaryData, bearBull }) => {
  const [selectedTab, setSelectedTab] =
    useState<keyof IStockTechnicalSummaryData>("ema");
  const [selectedOption, setSelectedOption] = useState<EmaSmaTypes>("EMA");
  const loginStatus = useAppSelector(selectLoginStatus);

  /**
   * Handle tab change with GA tracking for technical summary
   */
  const handleTabChange = (newTab: keyof IStockTechnicalSummaryData) => {
    // Find the tab position
    const tabPosition =
      STOCK_TECHNICAL_SUMMARY_TABS.findIndex((tab) => tab.value === newTab) + 1;

    // Get the tab label
    const tabLabel =
      STOCK_TECHNICAL_SUMMARY_TABS.find((tab) => tab.value === newTab)?.label ||
      newTab;

    // Track the tab click
    handleTechnicalSummaryTabDL(loginStatus, {
      sectionTitle: "Technical Summary",
      ctaText: tabLabel,
      tabPosition: tabPosition,
    });

    // Set the selected tab
    setSelectedTab(newTab);
  };
  const isMobile = useMediaQuery("(max-width: 768px)");

  const graphData = isMobile
    ? STOCK_TECHNICAL_SUMMARY_GRAPH_MOB
    : STOCK_TECHNICAL_SUMMARY_GRAPH;

  const barWidth = isMobile ? 5 : 10;
  const gap = isMobile ? 19 : 35;
  const totalBars = graphData.length;

  const graphBearBull =
    selectedOption === STOCK_TECHNICAL_TABS?.EMA
      ? technicalSummaryData["ema"]
      : technicalSummaryData["sma"];

  return (
    <div className="mt-1">
      <div className="flex gap-3.5" data-component-id="technical-summary">
        <BFLTabs
          tabs={STOCK_TECHNICAL_SUMMARY_TABS}
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
          color="text-bfl-blue"
          tabClassName="mr-3 md:mr-4 pb-[1px]"
          gapClassName="md:pb-[14px]"
        />
      </div>

      <div className={selectedTab !== "ema" ? "hidden" : "block"}>
        <div className="mt-4 w-full max-w-fit flex-col items-center pl-[6px] md:pl-0">
          <div className="mb-5 flex items-center justify-center gap-2 text-center md:mt-1.5">
            <div className="mr-2 text-base text-gray-900 md:text-[20px]">
              {"₹"+graphBearBull?.price}
            </div>
            <span
              className={`text-xs md:text-base ${
                (graphBearBull?.pChg ?? 0) < 0
                  ? "text-red-600"
                  : "text-green-500"
              }`}
            >
              {`${(graphBearBull?.pChg ?? 0) > 0 ? "+" : ""} ${graphBearBull?.pChg ?? 0} ${(graphBearBull?.pChg ?? 0) > 0 ? "▲" : "▼"}`}
            </span>
          </div>
          <BFLLazyLoad
            className="min-h-[145px] md:min-h-[285px]"
            threshold={0.3}
          >
            <StockTechnicalSummaryChart
              bearBull={technicalSummaryData["bearBull"]}
            />
          </BFLLazyLoad>
          <div
            className="mobile-wrap-stock flex items-center justify-between gap-4"
            style={{
              width: `${isMobile ? 380 : (barWidth + gap) * (totalBars - 1) + barWidth}px`,
            }}
          >
            <div className="flex min-w-[80px] flex-col items-start">
              <div className="text-base text-gray-900 md:text-lg">
                {bearBull?.bearish}
              </div>
              <div className="rounded bg-[#ffdede] px-3 py-1 text-xs text-red-600">
                Bearish
              </div>
            </div>
            <div className="flex min-w-[80px] flex-col items-end">
              <div className="text-base text-gray-900 md:text-lg">
                {bearBull?.bullish}
              </div>
              <div className="rounded bg-green-100 px-3 py-1 text-xs text-green-500">
                Bullish
              </div>
            </div>
          </div>
        </div>

        <div className="mt-[-12px]">
          <BFLToggleButtonWrapper>
            <BFLToggleButton
              active={selectedOption === STOCK_TECHNICAL_TABS?.EMA}
              onClick={() => setSelectedOption("EMA")}
            >
              EMA
            </BFLToggleButton>
            <BFLToggleButton
              active={selectedOption === STOCK_TECHNICAL_TABS?.SMA}
              onClick={() => setSelectedOption("SMA")}
            >
              SMA
            </BFLToggleButton>
          </BFLToggleButtonWrapper>
        </div>

        {selectedTab === "ema" && (
          <>
            <div
              style={{ display: selectedOption === "EMA" ? "block" : "none" }}
              data-ssr-ema-sma="EMA"
              data-testid="technical-summary-table-ema-wrapper"
            >
              <TechnicalSummaryTable
                selectedTab={selectedTab}
                data={technicalSummaryData["ema"]}
              />
            </div>
            <div
              style={{ display: selectedOption === "SMA" ? "block" : "none" }}
              data-ssr-ema-sma="SMA"
              data-testid="technical-summary-table-sma-wrapper"
            >
              <TechnicalSummaryTable
                selectedTab={selectedTab}
                data={technicalSummaryData["sma"]}
              />
            </div>
          </>
        )}
      </div>

      {STOCK_TECHNICAL_SUMMARY_TABS.filter((tab) => tab.value !== "ema").map(
        (tab) => (
          <div
            key={tab.value}
            style={{ display: selectedTab === tab.value ? "block" : "none" }}
          >
            <TechnicalSummaryTable
              selectedTab={tab.value}
              data={technicalSummaryData[tab.value]}
            />
          </div>
        ),
      )}
    </div>
  );
};

export default StockTechnicalSummaryClientWrapper;
